// Pure scratchpad → wire-DTO transform for the draft-save round-trip.
//
// WHY (#1599): extracted from `useDraftSave.ts` (which crossed the
//   400-line soft limit once the funnel-link injection landed) along its
//   real responsibility boundary — `useDraftSave` ORCHESTRATES the save
//   (lifecycle machine, snapshot, network call, store apply), while this
//   module is the deterministic body builder it feeds. Keeping the
//   transform here also gives the #1599 funnel-link injection a single,
//   testable home.
//
// @aiPurpose Build the `DraftSaveInput` wire body from a draft scratchpad.
// @aiInvariant (#1599) `leadId`/`dealId` ride the FIRST save only —
//   they are emitted under the same `includeClientToken === true` guard
//   that gates the create-time `clientToken`, and only when the scratchpad
//   carries them. Every `PUT` (update) omits both because `includeClientToken`
//   is `false`; an unlinked "+" draft omits both because the scratchpad
//   never carries them. #1596's backend binds the link at create time and
//   rejects a present link on update.

import type { DraftBox, DraftScratchpad } from './types.js';
import type { DraftSaveBoxInput, DraftSaveInput } from '@magimp/shared';
import { isSimpleKind } from '@magimp/shared';
import { kindHidesInsurance } from './insurance-visibility.js';

export function buildDraftSaveInput(
  d: DraftScratchpad,
  includeClientToken: boolean,
): DraftSaveInput {
  // WHY (#1306): SIMPLE requests forbid insurance and boxes at the wire
  //   level. Compute once here so the insurance triplet and boxes spread
  //   below defensively force the forbidden fields off — even if the
  //   scratchpad carries stale enabled state from a prior kind switch.
  // WHY (#1393): CONTAINER also hides insurance (same as SIMPLE), so its
  //   triplet must be forced off at the save layer too.
  const isSimple = isSimpleKind(d.kind);
  const hidesInsurance = kindHidesInsurance(d.kind);
  // WHY (#1446): the redesigned CONTAINER form owns NEITHER positions nor
  //   the Roads/Packaging selections. To preserve any hidden legacy
  //   positions/roads/packaging on a persisted CONTAINER row, OMIT those
  //   three keys entirely (omitted = preserve per #1447's tristate `items`
  //   contract and the `.optional()` roads/packaging draft-save fields).
  //   Sending an authoritative `[]` would wipe legacy rows — never do that
  //   for CONTAINER. New CONTAINER drafts simply never create them.
  const isContainer = d.kind === 'CONTAINER';
  // WHY (#1448): the redesigned STANDARD form owns request-level
  //   weight/volume but NOT positions, insurance, or boxes. To preserve any
  //   hidden legacy positions/insurance/boxes on a persisted STANDARD row,
  //   OMIT those keys (omitted = preserve per #1447's tristate `items`,
  //   #1052's tristate insurance, and #599's tristate `boxes` contracts).
  //   STANDARD keeps owning roads/packaging (the basics card still renders
  //   them inline).
  const isStandard = !isSimple && !isContainer;
  // WHY (#2116): CONTAINER now also owns request-level weight/volume (its
  //   redesigned form captures them like STANDARD); SIMPLE still derives the
  //   wire weight/volume from items/boxes. SEND them for STANDARD OR CONTAINER.
  const ownsRequestTotals = isStandard || isContainer;
  // WHY (#1448): never SEND an authoritative `boxes` for STANDARD — a
  //   legacy STANDARD row hydrated with persisted boxes flips
  //   `boxesSectionOpen` true, which would otherwise wipe-or-overwrite the
  //   hidden boxes. Boxes are now only ever omitted (no kind renders them).
  const ownsBoxes = !isSimple && !isStandard && (d.boxes.length > 0 || d.boxesSectionOpen);
  return {
    name: d.name,
    // WHY (#700): destinations replaces the freeform `route` on every
    //   write. `routeFrom` is server-hardcoded, `routeTo` is the
    //   denormalised mirror of the selected city names.
    destinations: d.destinations.map((dest) => ({
      arrivalCityId: dest.arrivalCityId,
      sortOrder: dest.sortOrder,
    })),
    // WHY (#701): both new keys round-trip on every draft save. The
    //   scratchpad carries them; the server-side refine rejects a
    //   mismatched pair (CONTAINER ↔ non-null `transportTypeId`).
    kind: d.kind,
    transportTypeId: d.transportTypeId,
    // WHY (#1057): always send `deliveryAddress` (including `null`) so
    //   a manager can clear a previously-saved value. The wire's
    //   draft-save trims/normalises the string and rejects a non-null
    //   value on STANDARD. CONTAINER drafts may persist `null` until
    //   the manager fills it — send-readiness reasserts non-empty on
    //   the transition to IN_WORK.
    deliveryAddress: d.deliveryAddress,
    // WHY (#1393): always send `pickupAddress` (including `null`) so a
    //   manager can clear a previously-saved value. The wire's draft-save
    //   trims/normalises the string and rejects a non-null value on
    //   STANDARD; send-readiness reasserts non-empty for
    //   CONTAINER on the transition to IN_WORK.
    pickupAddress: d.pickupAddress,
    // WHY (#1061): send the insurance triplet so the wire's cross-field
    //   invariant runs on every save. The store keeps the triplet coherent
    //   so the body never lands in the rejected `false → non-null` shape.
    // WHY (#1306): SIMPLE forbids insurance — force false/null/null.
    // WHY (#1393): CONTAINER also hides insurance — same forced-off rule.
    // WHY (#1448): OMIT the triplet entirely for STANDARD (conditional
    //   spread below). The redesigned STANDARD form no longer renders
    //   insurance, so a save must not own it — omitting the three keys
    //   preserves any hidden legacy insurance per #1052's tristate contract.
    //   Sending a forced `false`/`null` would wipe it.
    ...(isStandard
      ? {}
      : {
          insuranceRequested: hidesInsurance ? false : (d.insuranceRequested ?? false),
          insuranceCargoValue:
            !hidesInsurance && d.insuranceRequested ? (d.insuranceCargoValue ?? null) : null,
          insuranceCargoCurrency:
            !hidesInsurance && d.insuranceRequested ? (d.insuranceCargoCurrency ?? null) : null,
        }),
    // WHY (#1448 / #2116): SEND the request-level totals for STANDARD AND
    //   CONTAINER. The wire treats them as authoritative (#1447); `null` is a
    //   legal intermediate DRAFT value (send-readiness reasserts > 0). Only
    //   SIMPLE omits them, deriving the wire weight/volume from its items/boxes.
    ...(ownsRequestTotals ? { weight: d.totalWeight, volume: d.totalVolume } : {}),
    // WHY (#996): request-level Roads/Packaging multiselects. Sent for
    //   SIMPLE/STANDARD — empty arrays are the no-selection signal and
    //   round-trip with the wire layer's `.optional()` replace-the-whole-
    //   set semantics.
    // WHY (#1446): OMITTED for CONTAINER so a redesigned CONTAINER save
    //   preserves hidden legacy roads/packaging by omission instead of
    //   wiping them with an authoritative `[]`. STANDARD still owns them.
    ...(isContainer
      ? {}
      : {
          roadOptionIds: [...(d.roadOptionIds ?? [])],
          packagingOptionIds: [...(d.packagingOptionIds ?? [])],
        }),
    // WHY (#1446): OMIT `items` for CONTAINER — its form has no Positions
    //   section, so a save must not own items (omitting preserves persisted
    //   legacy positions per #1447's tristate `items` contract).
    // WHY (#1448): OMIT `items` for STANDARD too — its totals live on the
    //   request-level weight/volume above, and its form dropped Positions.
    //   Only SIMPLE still owns and sends its items.
    ...(isSimple ? { items: buildDraftItems(d) } : {}),
    // WHY (#599): tristate ownership — the editor owns the boxes field only
    //   when the boxes section was opened.
    // WHY (#1306): SIMPLE forbids boxes — never include the key.
    // WHY (#1448): STANDARD also drops boxes — omitting preserves hidden
    //   legacy boxes per #599's tristate contract (see `ownsBoxes`). No kind
    //   sends an authoritative `boxes` anymore.
    ...(ownsBoxes ? { boxes: d.boxes.map(buildDraftBoxPayload) } : {}),
    ...(includeClientToken ? { clientToken: d.clientToken } : {}),
    // WHY (#1599): inject the pre-bound funnel link on the FIRST save only.
    //   `includeClientToken === true` is exactly the create POST (#521's
    //   `persistedId === null` branch), so this rides the same gate as the
    //   create-time idempotency token. Each id is included only when the
    //   scratchpad carries it — an unlinked "+" draft omits both (→ unlinked
    //   request), and every subsequent PUT omits both (#1596's update path
    //   rejects a present link). Coalesce `undefined` → `null` so the strict
    //   wire schema never sees an `undefined` value under the present key.
    ...(includeClientToken && d.leadId !== undefined ? { leadId: d.leadId ?? null } : {}),
    ...(includeClientToken && d.dealId !== undefined ? { dealId: d.dealId ?? null } : {}),
  };
}

function buildDraftBoxPayload(b: DraftBox, idx: number): DraftSaveBoxInput {
  const payload: DraftSaveBoxInput = {
    clientBoxId: b.clientBoxId,
    sortOrder: idx,
    count: b.count,
    weightKg: b.weightKg,
    volumeM3: b.volumeM3,
    volumeMode: b.volumeMode,
    widthCm: b.widthCm,
    depthCm: b.depthCm,
    heightCm: b.heightCm,
    items: b.items.map((it, itIdx) => {
      const itemPayload: DraftSaveBoxInput['items'][number] = {
        clientBoxItemId: it.clientBoxItemId,
        sortOrder: itIdx,
        // WHY (#704): always send the grouping ordinal so the wire
        //   value is authoritative on every save round-trip. Pre-#704
        //   drafts default to `0` in the store init / migration
        //   read path, so this is a no-op for legacy data.
        groupOrdinal: it.groupOrdinal,
        name: it.name,
        brand: it.brand,
        // WHY (#1055): always send `material`, including when empty, so a
        //   nested item's material can be cleared. The backend nested
        //   update path skips the field when undefined.
        material: it.material,
        description: it.description,
        quantity: it.quantity,
        photos: [...it.photos],
      };
      if (it.id !== null) itemPayload.id = it.id;
      return itemPayload;
    }),
  };
  if (b.id !== null) payload.id = b.id;
  return payload;
}

// WHY (#1446): extracted from `buildDraftSaveInput` so the CONTAINER
//   omit-`items` conditional spread stays a single line and `useDraftSave`
//   keeps its existing item-serialization behavior verbatim for
//   SIMPLE/STANDARD. Only callers that OWN positions (non-CONTAINER) build
//   this array; CONTAINER omits the `items` key entirely (preserve-by-
//   omission per #1447's tristate contract).
function buildDraftItems(d: DraftScratchpad): NonNullable<DraftSaveInput['items']> {
  return d.items.map((it) => {
    const isPersisted = /^it-\d{4}$/.test(it.id);
    const base = {
      clientItemId: it.id,
      name: it.name,
      brand: it.brand,
      // WHY (#1055): always send `material`, including when empty. The
      //   backend's draft-update path skips the field when undefined,
      //   so omitting it would prevent users from clearing a
      //   previously-saved material value.
      material: it.material,
      description: it.description,
      weight: it.weight,
      volume: it.volume,
      quantity: it.quantity,
      // WHY (#1056): always send `productQuantity` (null when blank)
      //   so a manager can clear a previously-saved value. The
      //   backend's draft-update path skips when undefined; sending
      //   the explicit value lets a tristate save round-trip cleanly.
      productQuantity: it.productQuantity,
      photos: [...it.photos],
      // WHY (#590): always echo `dimensionsMode` so the backend can
      //   opt in to the expanded response shape via mutation-body
      //   detection; without it, a save that toggled the mode off
      //   would receive a legacy-shaped response and silently lose
      //   the package mapping.
      dimensionsMode: it.dimensionsMode,
      packages: it.packages.map((p, pkgIdx) => {
        const pkg: {
          clientPackageId: string;
          id?: string;
          sortOrder: number;
          widthCm: number;
          depthCm: number;
          heightCm: number;
          weightKg: number;
          count: number;
        } = {
          clientPackageId: p.clientPackageId,
          sortOrder: pkgIdx,
          widthCm: p.widthCm,
          depthCm: p.depthCm,
          heightCm: p.heightCm,
          weightKg: p.weightKg,
          count: p.count,
        };
        if (p.id !== null) pkg.id = p.id;
        return pkg;
      }),
    };
    return isPersisted ? { ...base, id: it.id } : base;
  });
}
